import { ExpenseApiClient } from '@/clients/expenseApiClient';
import { ExpenseReimbursement, ExpenseStatus } from '@/types/expense';
import { InvoiceUploadService } from './invoiceUploadService';

export class ExpenseService {
  private apiClient: ExpenseApiClient;
  private uploadService: InvoiceUploadService;

  constructor() {
    this.apiClient = new ExpenseApiClient();
    this.uploadService = new InvoiceUploadService();
  }

  /**
   * Upload a receipt file using existing upload infrastructure,
   * then create expense record via API.
   */
  async uploadReceipt(file: File, employeeId?: string): Promise<ExpenseReimbursement> {
    // 1. Upload file to invoices bucket (reuses existing upload logic)
    const { filePath } = await this.uploadService.uploadFileOnly(file);

    // 2. Create expense record via API (creates financial_document + expense_reimbursement)
    return this.apiClient.createExpense(filePath, file.name, employeeId);
  }

  async listExpenses(filters?: { status?: ExpenseStatus; employeeId?: string }): Promise<ExpenseReimbursement[]> {
    return this.apiClient.listExpenses(filters);
  }

  async getExpense(expenseId: string): Promise<ExpenseReimbursement> {
    return this.apiClient.getExpense(expenseId);
  }

  async saveManualData(
    expenseId: string,
    documentData: Record<string, unknown>,
    expenseData: Record<string, unknown>
  ): Promise<ExpenseReimbursement> {
    return this.apiClient.saveManualData(expenseId, documentData, expenseData);
  }

  async approveSuggestion(expenseId: string): Promise<ExpenseReimbursement> {
    return this.apiClient.approveSuggestion(expenseId);
  }

  async approveAndQueuePayment(expenseId: string): Promise<{ expense: ExpenseReimbursement; payable: any }> {
    return this.apiClient.approveAndQueuePayment(expenseId);
  }

  async bulkApproveAndQueuePayment(expenseIds: string[]): Promise<{
    successes: { expense: ExpenseReimbursement; payable: any }[];
    failures: { expenseId: string; error: string }[];
  }> {
    return this.apiClient.bulkApproveAndQueuePayment(expenseIds);
  }

  async approveAndMarkPaidExternally(expenseId: string, paidDate: string, actualSekAmount?: number): Promise<ExpenseReimbursement> {
    return this.apiClient.approveAndMarkPaidExternally(expenseId, paidDate, actualSekAmount);
  }

  async bulkApproveAndMarkPaidExternally(expenseIds: string[], paidDate: string): Promise<{
    successes: ExpenseReimbursement[];
    failures: { expenseId: string; error: string }[];
  }> {
    return this.apiClient.bulkApproveAndMarkPaidExternally(expenseIds, paidDate);
  }

  async updateEmployee(expenseId: string, employeeId: string | null): Promise<ExpenseReimbursement> {
    return this.apiClient.updateEmployee(expenseId, employeeId);
  }

  async rejectExpense(expenseId: string, reason: string): Promise<ExpenseReimbursement> {
    return this.apiClient.rejectExpense(expenseId, reason);
  }
}
